/**
 * UserAvatar - Reusable avatar component that displays profile picture or emoji fallback
 *
 * Usage:
 *   <UserAvatar user={friend} size="md" />
 *   <UserAvatar user={currentUser} size="sm" className="mr-2" />
 */
import { useTranslation } from 'react-i18next';
import Icon from './shared/Icon';

const SIZES = {
  xs: 'w-5 h-5',      // 20px - for compact chips
  sm: 'w-7 h-7',      // 28px - for list items
  md: 'w-10 h-10',    // 40px - for cards
  lg: 'w-14 h-14',    // 56px - for profile headers
  xl: 'w-20 h-20',    // 80px - for large profile views
};

const EMOJI_SIZES = {
  xs: 'text-sm',
  sm: 'text-xl',
  md: 'text-2xl',
  lg: 'text-3xl',
  xl: 'text-4xl',
};

function UserAvatar({
  user,
  size = 'sm',
  className = '',
  showOnlineStatus = false,
}) {
  const { t } = useTranslation();
  if (!user) {
    return (
      <span className={`flex-shrink-0 ${className}`}>
        <Icon name="user" className={SIZES[size] || SIZES.sm} />
      </span>
    );
  }

  const sizeClass = SIZES[size] || SIZES.sm;
  const emojiSize = EMOJI_SIZES[size] || EMOJI_SIZES.sm;
  const profilePicture = user.profilePicture || user.profile_picture;

  if (profilePicture) {
    return (
      <div className={`relative flex-shrink-0 ${className}`}>
        <img
          src={profilePicture}
          alt={user.name || t('userAvatar.altFallback')}
          className={`${sizeClass} rounded-full object-cover`}
        />
        {showOnlineStatus && user.isOnline && (
          <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-success border-2 border-white rounded-full" />
        )}
      </div>
    );
  }

  return (
    <span className={`${emojiSize} flex-shrink-0 ${className}`}>
      {user.avatar || <Icon name="user" className={SIZES[size] || SIZES.sm} />}
    </span>
  );
}

export default UserAvatar;
